import React, { useEffect, useMemo, useState } from "react";
import { api, formatDetail } from "@/lib/api";
import { toast } from "sonner";
import { X, Search, Loader2, Save, Trash2, Upload } from "lucide-react";

// Modal-style panel: manage the polling units assigned to a specific agent.
export default function AdminAssignments({ user, onClose }) {
  const [assigned, setAssigned] = useState([]);        // [{id,name,code,path}]
  const [search, setSearch] = useState("");
  const [hits, setHits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);

  const assignedIds = useMemo(() => new Set(assigned.map(a => a.id)), [assigned]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/users/${user.id}/assignments`);
        if (!cancelled) setAssigned(data || []);
      } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [user.id]);

  useEffect(() => {
    const q = search.trim();
    if (q.length < 2) { setHits([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/locations/search", { params: { q, level: "polling_unit", limit: 20 }});
        setHits(data || []);
      } catch {}
    }, 200);
    return () => clearTimeout(t);
  }, [search]);

  const addPU = (pu) => {
    if (assignedIds.has(pu.id)) return toast.info("Already assigned");
    setAssigned(prev => [...prev, { id: pu.id, name: pu.name, code: pu.code, path: pu.path }]);
    setSearch(""); setHits([]);
  };
  const removePU = (id) => setAssigned(prev => prev.filter(a => a.id !== id));

  // Bulk paste: user drops a CSV / newline-separated list of polling-unit codes.
  // We resolve each code via /locations/search and skip unknown ones.
  const runBulkAdd = async () => {
    const raw = bulkText.trim();
    if (!raw) return toast.error("Paste at least one polling-unit code");
    // Accept commas, newlines, tabs, spaces as separators. Ignore blanks + header row.
    const rawCodes = raw
      .split(/[\s,;\n\r\t]+/)
      .map(t => t.trim().toUpperCase())
      .filter(Boolean)
      .filter(t => t.toLowerCase() !== "code" && t.toLowerCase() !== "pu_code");
    if (!rawCodes.length) return toast.error("No valid codes detected");
    setBulkBusy(true);
    const already = new Set(assigned.map(a => a.code.toUpperCase()));
    let added = 0, missing = [];
    try {
      // resolve in small batches so we don't hammer the search endpoint
      for (const code of rawCodes) {
        if (already.has(code)) continue;
        try {
          const { data } = await api.get("/locations/search", { params: { q: code, level: "polling_unit", limit: 3 }});
          const match = (data || []).find(h => (h.code || "").toUpperCase() === code);
          if (match) {
            setAssigned(prev => prev.some(a => a.id === match.id) ? prev : [...prev, { id: match.id, name: match.name, code: match.code, path: match.path }]);
            already.add(code); added++;
          } else {
            missing.push(code);
          }
        } catch { missing.push(code); }
      }
      if (added) toast.success(`Added ${added} polling unit${added===1?"":"s"}${missing.length?` · ${missing.length} not found`:""}`);
      else if (missing.length) toast.error(`None of the ${missing.length} codes matched a polling unit`);
      if (missing.length) toast.info(`Not found: ${missing.slice(0,10).join(", ")}${missing.length>10?` and ${missing.length-10} more`:""}`, { duration: 8000 });
      setBulkText(""); setBulkOpen(false);
    } finally { setBulkBusy(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/users/${user.id}/assignments`, { polling_unit_ids: assigned.map(a => a.id) });
      const n = data?.notified || {};
      const changed = (n.added || 0) + (n.removed || 0);
      if (!changed) toast.success(`Saved — no changes for ${user.email}`);
      else if (n.email) toast.success(`Saved ${assigned.length} assignment${assigned.length===1?"":"s"} · ${user.email} emailed (${n.added} added, ${n.removed} removed)${n.sms ? " + SMS" : ""}`);
      else toast.warning(`Saved ${assigned.length} assignment${assigned.length===1?"":"s"}, but the email to ${user.email} could not be sent — let them know directly.`, { duration: 8000 });
      onClose?.();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6" data-testid="assignments-modal">
      <div className="w-full max-w-3xl border border-white/10 bg-[#0A0A0A] text-white flex flex-col max-h-[90vh]">
        <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Assign Polling Units</div>
            <div className="text-lg font-medium mt-0.5 truncate">{user.email}</div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-white/10" data-testid="assignments-close-btn"><X size={16}/></button>
        </div>

        <div className="p-5 border-b border-white/10 relative">
          <div className="flex items-center gap-2 border border-white/20 px-3 py-2">
            <Search size={14} className="text-zinc-500"/>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search polling units by name or code…"
              className="flex-1 bg-transparent outline-none text-sm" data-testid="assignments-search"/>
            <button type="button" onClick={()=>setBulkOpen(v=>!v)} data-testid="assignments-bulk-toggle"
              className="text-[10px] uppercase tracking-[0.2em] text-emerald-300 hover:text-emerald-200 border border-emerald-500/30 hover:border-emerald-500/60 px-2 py-1 flex items-center gap-1">
              <Upload size={11}/> Bulk paste
            </button>
          </div>
          {bulkOpen && (
            <div className="mt-3 border border-emerald-500/30 bg-emerald-500/[0.04] p-3" data-testid="bulk-assign-panel">
              <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-300">Paste 100+ polling-unit codes</div>
              <div className="text-[10px] text-zinc-400 mt-1">One code per line, or comma/space separated. A leading <span className="text-white">code</span> or <span className="text-white">pu_code</span> header row is ignored.</div>
              <textarea value={bulkText} onChange={e=>setBulkText(e.target.value)} rows={6} data-testid="bulk-assign-textarea"
                placeholder={"A010101\nA010102\nB050201\nH040308\n…"}
                className="w-full mt-2 bg-transparent border border-white/15 p-2 text-sm tabular outline-none resize-y focus:border-emerald-400"/>
              <div className="flex items-center justify-end gap-2 mt-2">
                <button onClick={()=>{ setBulkOpen(false); setBulkText(""); }} disabled={bulkBusy}
                  className="px-3 py-1.5 border border-white/20 text-[10px] uppercase tracking-[0.2em] hover:bg-white/10">Cancel</button>
                <button onClick={runBulkAdd} disabled={bulkBusy} data-testid="bulk-assign-add-btn"
                  className="px-3 py-1.5 bg-emerald-500 text-black text-[10px] uppercase tracking-[0.2em] font-semibold hover:bg-emerald-400 disabled:opacity-40 flex items-center gap-1">
                  {bulkBusy ? <Loader2 size={11} className="animate-spin"/> : <Upload size={11}/>} Add matches
                </button>
              </div>
            </div>
          )}
          {hits.length > 0 && !bulkOpen && (
            <div className="absolute left-5 right-5 mt-1 z-10 bg-[#0F0F12] border border-white/20 max-h-72 overflow-y-auto shadow-2xl" data-testid="assignments-search-results">
              {hits.map(h => (
                <button key={h.id} type="button" onClick={()=>addPU(h)} disabled={assignedIds.has(h.id)}
                  data-testid={`assignments-hit-${h.code}`}
                  className={`w-full text-left px-3 py-2 border-b border-white/5 flex items-start gap-3 ${assignedIds.has(h.id)?"opacity-40 cursor-not-allowed":"hover:bg-white/10"}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white">{h.name}</div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                      <span className="text-emerald-400 tabular">{h.code}</span> · {h.path}
                    </div>
                  </div>
                  <span className="text-[9px] uppercase tracking-widest text-zinc-500">{assignedIds.has(h.id)?"Assigned":"Add +"}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">Currently Assigned · {assigned.length}</div>
          {loading ? (
            <div className="text-zinc-500 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin"/> Loading…</div>
          ) : assigned.length ? (
            <div className="divide-y divide-white/5 border border-white/10">
              {assigned.map(a => (
                <div key={a.id} className="px-4 py-3 flex items-center gap-3" data-testid={`assigned-row-${a.code}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-emerald-400 tabular text-xs">{a.code}</div>
                    <div className="text-sm mt-0.5 truncate">{a.name}</div>
                    <div className="text-[10px] text-zinc-500 mt-0.5 truncate">{a.path}</div>
                  </div>
                  <button onClick={()=>removePU(a.id)} data-testid={`assigned-remove-${a.code}`}
                    className="p-1.5 text-zinc-500 hover:text-red-400"><Trash2 size={14}/></button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-zinc-500 text-sm border border-dashed border-white/10 p-6 text-center">
              No polling units assigned yet. Search above to add.
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-white/10 flex items-center gap-3 justify-end">
          <button onClick={onClose} className="px-4 py-2 border border-white/20 text-xs uppercase tracking-[0.2em] hover:bg-white/10">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="assignments-save-btn"
            className="px-4 py-2 bg-emerald-500 text-black text-xs uppercase tracking-[0.2em] font-semibold hover:bg-emerald-400 disabled:opacity-40 flex items-center gap-2">
            {saving ? <Loader2 size={13} className="animate-spin"/> : <Save size={13}/>} Save assignments
          </button>
        </div>
      </div>
    </div>
  );
}
